import { AsyncPipe, DOCUMENT, Location, NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
  QueryList,
  Renderer2,
  signal,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router, RouterLink, RouterOutlet } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { BehaviorSubject, combineLatest, map, mergeMap, Observable, of, tap } from 'rxjs';
import { FilterComponent } from 'src/app/pages/search-results/filter/filter.component';
import { GenericDialogComponent } from 'src/app/_shared/dialogs/generic-dialog/generic-dialog.component';
import { HeaderSearchService } from 'src/app/_shared/components/header/header-search/header-search.service';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { OpenSnackBarService } from 'src/app/common/open-snack-bar/open-snack-bar.service';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { baseUrl, DateRange, getDefaultModalSettings, Key, parseArray } from 'src/app/common/utils/helpers';
import { AccessibilityCategory, Category, Qualification, SharryKind, SortDirection } from 'src/types/graphql';
import {
  centerOfMunicipalities,
  centerOfProvinces,
  centerOfRegions,
  municipalities,
  places,
  regions,
} from 'utils/places-utility';
import { SearchResultsFilterablesService } from 'utils/search-results-filterable';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { SearchEnginePositionService } from './search-engine-position.service';
import {
  GetAccessibilityCategoriesGQL,
  GetBannerByKeywordGQL,
  GetQualificationsGQL,
  GetSearchCategoriesGQL,
} from './search-results.query.generated';
import { BasePlusComponent } from 'src/app/_shared/components/base/base-plus.component';
import { RouteTranslatePipe } from 'src/app/_shared/pipes/route-translate.pipe';
import { CanComponentDeactivate } from '../../_shared/guards/can-component-deactivate';
import { ParamsService } from './params/params.service';

@Pipe({
  name: 'sharryArraify',
  standalone: true,
})
export class ArraifyPipe implements PipeTransform {
  transform(value: string | string[]): string[] {
    return value ? (typeof value === 'string' ? [value] : [...value]) : [];
  }
}

@Pipe({
  name: 'sharrySourceName',
  standalone: true,
})
export class SourceNamePipe implements PipeTransform {
  constructor() {}

  transform<T>(id: string, source: T[]) {
    const obj = (
      source as
        | Array<Partial<Category> | Partial<AccessibilityCategory> | Partial<SharryKind>>
        | Array<Partial<Qualification>>
    ).find(c => c.id === id);
    switch (obj?.__typename) {
      case 'SharryKind':
        return obj?.name;
      case 'Qualification':
        return obj?.text;
      default:
        return obj?.name;
    }
  }
}

@Component({
  selector: 'sharry-search-engine',
  standalone: true,
  templateUrl: './search-engine.component.html',
  styleUrls: ['./search-engine.component.scss'],
  imports: [
    NgClass,
    AsyncPipe,
    RouterOutlet,
    RouterLink,
    MatButtonModule,
    I18NextModule,
    SourceNamePipe,
    IconComponent,
    ArraifyPipe,
    GenericDialogComponent,
    FilterComponent,
    RouteTranslatePipe,
    HeaderComponent,
  ],
  providers: [ParamsService],
})
export class SearchEngineComponent extends BasePlusComponent implements CanComponentDeactivate, OnInit, OnDestroy {
  @ViewChild('mobileFiltersDialog', { static: true }) mobileFiltersDialog: TemplateRef<any>;
  @ViewChildren(FilterComponent) filterChildren: QueryList<FilterComponent>;
  activeFilter: string[];
  // FILTERS
  typeOptions: { id: SearchType; name: string; icon: string }[] = [
    {
      id: 'wonder',
      name: 'search-results.filters.wonders',
      icon: 'https://media.sharry.land/app/images/families/ic-wonder.webp',
    },
    {
      id: 'event',
      name: 'search-results.filters.events',
      icon: 'https://media.sharry.land/app/images/families/ic-event.webp',
    },
    {
      id: 'users',
      name: 'search-results.filters.users',
      icon: 'https://media.sharry.land/app/images/families/ic-user.webp',
    },
  ];

  typeSharryOptions: { id: SearchType; name: string; icon: string }[] = [
    {
      id: 'tour',
      name: 'search-results.filters.tours',
      icon: 'https://media.sharry.land/app/images/families/ic-tour.webp',
    },
    {
      id: 'experience',
      name: 'search-results.filters.experiences',
      icon: 'https://media.sharry.land/app/images/families/ic-experience.webp',
    },
    {
      id: 'item',
      name: 'search-results.filters.items',
      icon: 'https://media.sharry.land/app/images/families/ic-item.webp',
    },
    // {
    //   id: 'rental',
    //   name: 'search-results.filters.rentals',
    //   icon: 'https://media.sharry.land/app/images/families/ic-rental.webp',
    // },
    {
      id: 'accommodation',
      name: 'search-results.filters.accommodation',
      icon: 'https://media.sharry.land/app/images/families/ic-accommodation.webp',
    },
  ];

  typesPluralMapping = {
    event: {
      '=1': 'event',
      other: 'events',
    },
    wonder: {
      '=1': 'wonder',
      other: 'wonders',
    },
    sharry: {
      '=1': 'sharry',
      other: 'sharries',
    },
    users: {
      '=1': 'user',
      other: 'users',
    },
  };

  private oldQueryParams: Params;
  searchCategories$: Observable<Partial<Category>[]>;
  accessibilityCategories$: Observable<Partial<AccessibilityCategory>[]>;
  searchQualifications$: Observable<Partial<Qualification>[]>;
  types$: Observable<boolean>;
  params$: Observable<Params>;
  paramsSubj: BehaviorSubject<Params> = new BehaviorSubject(null);
  dialog: MatDialogRef<any>;
  _types = signal<string[]>([]);

  busy = true;
  showBack = false;

  constructor(
    private getBannerByKeywordGQL: GetBannerByKeywordGQL,
    private getSearchCategoriesGQL: GetSearchCategoriesGQL,
    private getAccessibilityCategoriesGQL: GetAccessibilityCategoriesGQL,
    private getQualificationsGQL: GetQualificationsGQL,
    public route: ActivatedRoute,
    private openDialogService: OpenDialogService,
    private router: Router,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private routeTranslateService: RouteTranslateService,
    public searchResultsFilterablesService: SearchResultsFilterablesService,
    private searchEnginePositionService: SearchEnginePositionService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
    private cd: ChangeDetectorRef,
    private location: Location,
    private headerSearchService: HeaderSearchService,
    private openSnackBarService: OpenSnackBarService,
    private paramsService: ParamsService,
  ) {
    super();
    this.subscriptions.push(
      combineLatest([this.route.queryParams, this.route.params, this.route.data])
        .pipe(
          mergeMap(([_queryParams, pathParams, routeData]) => {
            if (_queryParams?.showBack && this.isMobile()) {
              this.showBack = true;
            }
            const keyword = pathParams.location || pathParams.category || pathParams.keyword;
            console.log('keyword', keyword);
            if (keyword) {
              return this.getBannerByKeywordGQL.fetch({ keyword: keyword }).pipe(
                map(r => {
                  const banner = r.data?.searchBanner;
                  console.log('[SSR] Banner trovato per keyword:', keyword, banner ? banner : 'Nessun banner trovato');
                  return {
                    _queryParams: _queryParams,
                    pathParams: pathParams,
                    routeData: routeData,
                    banner: banner,
                  };
                }),
              );
            } else {
              return of({ _queryParams: _queryParams, pathParams: pathParams, routeData: routeData, banner: null });
            }
          }),
          map(({ _queryParams, pathParams, routeData, banner }) => {
            const hasPath = !!Object.keys(pathParams || {})?.filter(k => !['lang', 'list', 'map'].includes(k)).length;
            let queryParams = { ...(_queryParams || {}) };
            if (Object.keys(routeData || {})?.length) {
              const types = (routeData.types || [])
                .map(
                  (type: string) =>
                    this.searchResultsFilterablesService[
                      (type + 'FilterableIds') as Key<SearchResultsFilterablesService>
                    ],
                )
                .flat();
              delete routeData.title;
              delete routeData.description;
              delete routeData.keywords;
              delete routeData.refresh;
              delete routeData.types;
              queryParams = { ...queryParams, types, ...(routeData || {}) };
              delete queryParams.isRegion;
              delete queryParams.isProvince;
              delete queryParams.isMunicipality;
              delete queryParams.isBanner;
              if (banner) {
                if (banner?.url) {
                  this.paramsService.setKindsFromUrlTypes(banner?.url[0]);
                }
                this.metadataService.setTitle(banner?.metaTag?.title);
                this.metadataService.setDescription(banner?.metaTag?.description);
                this.metadataService.setTags([
                  { property: 'og:title', content: banner?.metaTag?.title },
                  { property: 'og:url', content: baseUrl + this.location.path() },
                  {
                    property: 'og:description',
                    content: banner?.metaTag?.description || ' ',
                  },
                  {
                    property: 'og:image',
                    content: banner?.image?.url,
                  },
                  {
                    property: 'og:image:url',
                    content: banner?.image?.url,
                  },
                  { property: 'og:image:width', content: '600' },
                  { property: 'og:image:height', content: '600' },
                  { property: 'og:image:type', content: 'image/*' },
                ]);
              }
            }

            if (Object.keys(routeData || {})?.length && pathParams?.category) {
              this.paramsService.removeTypes();
              const cat = banner?.categoryId;
              if (cat) {
                if (!queryParams.searchCategories) {
                  queryParams.searchCategories = [cat];
                } else if (typeof queryParams.searchCategories === 'string' && queryParams.searchCategories !== cat) {
                  queryParams.searchCategories = [cat, queryParams.searchCategories];
                } else if (!queryParams.searchCategories.includes(cat)) {
                  queryParams.searchCategories.unshift(cat);
                }
              }
            }
            if (Object.keys(routeData || {})?.length && pathParams?.keyword) {
              queryParams.banner = pathParams.keyword;

              const url = new URL('https://www.sharry.land' + banner?.url);
              url.searchParams.forEach((value, key) => {
                if (queryParams[key]) {
                  if (typeof queryParams[key] === 'string') {
                    queryParams[key] = [queryParams[key], value];
                  } else {
                    queryParams[key] = [...queryParams[key], value];
                  }
                } else {
                  queryParams[key] = value;
                }
              });
              delete queryParams.v;
            }
            if (Object.keys(routeData || {})?.length && pathParams?.location) {
              if (routeData.isRegion) {
                queryParams.value = regions[pathParams.location];
                queryParams.region = regions[pathParams.location];
                const center = centerOfRegions.find(c => c.value === queryParams.value);
                queryParams.latitude = center?.latitude;
                queryParams.longitude = center?.longitude;
              }
              if (routeData?.isProvince) {
                queryParams.value = places[pathParams.location];
                queryParams.province = places[pathParams.location];

                const center = centerOfProvinces.find(c => c.value === queryParams.value);
                queryParams.latitude = center?.latitude;
                queryParams.longitude = center?.longitude;
              }
              if (routeData?.isMunicipality) {
                queryParams.value = municipalities[pathParams.location];
                queryParams.municipality = municipalities[pathParams.location];
                queryParams.distance = 50;
                const center = centerOfMunicipalities.find(c => c.value === queryParams.value);
                queryParams.latitude = center?.latitude;
                queryParams.longitude = center?.longitude;
              }
              queryParams.banner = pathParams.location;
            }
            Object.keys(routeData).forEach(k => delete routeData[k]);
            if (hasPath) {
              delete queryParams?.additional;
              this.router.navigate([this.routeTranslateService.translateRoute('search-results')], {
                queryParams: {
                  ...(this.paramsSubj?.value || {}),
                  ...queryParams,
                },
                skipLocationChange: true,
              });
            }
            delete queryParams.signin;
            delete queryParams.signup;
            return queryParams;
          }),
          tap(() => (this.busy = false)),
        )
        .subscribe(this.paramsSubj),
    );
    this.params$ = this.paramsSubj.asObservable();
    this.searchCategories$ = this.getSearchCategoriesGQL
      .fetch({
        sort: {
          key: 'sort',
          direction: SortDirection.Asc,
        },
      })
      .pipe(map(r => r.data.results));
    this.accessibilityCategories$ = this.getAccessibilityCategoriesGQL
      .fetch({
        sort: {
          key: 'sort',
          direction: SortDirection.Asc,
        },
      })
      .pipe(map(r => r.data.results));
    this.searchQualifications$ = this.getQualificationsGQL
      .fetch({
        sort: {
          key: 'sort',
          direction: SortDirection.Asc,
        },
      })
      .pipe(map(r => r.data.results));
    this.types$ = this.searchResultsFilterablesService.init(this.translateService.language);
  }

  ngOnInit(): void {
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
    this.paramsService.getTypes().subscribe(res => {
      this.selectedTypes = this.paramsService.loadSelectedTypes();
      if (res && res.length > 0) {
        this._types.set(res);
      } else {
        const savedTypes = this.paramsService.loadFromStorage();
        this._types.set(savedTypes);
      }
    });
  }

  showAccessibility(obj: any) {
    // Initialize counters and flags
    let nonEmptyKeys = 0;
    let isItemNonEmpty = false;
    let isUsersNonEmpty = false;
    // Iterate through the object keys, ignoring "sharry"
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key) && key !== 'sharry') {
        if (obj[key].length > 0) {
          nonEmptyKeys++;
          if (key === 'item') {
            isItemNonEmpty = true;
          }
          if (key === 'users') {
            isUsersNonEmpty = true;
          }
        }
      }
    }
    // Return false if only "item" or "users" is non-empty
    if (nonEmptyKeys === 1 && (isItemNonEmpty || isUsersNonEmpty)) {
      return false;
    }
    // Return false if both "item" and "users" are non-empty and there are no other non-empty keys
    if (isItemNonEmpty && isUsersNonEmpty && nonEmptyKeys === 2) {
      return false;
    }
    // Return true otherwise
    return true;
  }

  canDeactivate(): boolean | Promise<boolean> | Observable<boolean> {
    this.searchEnginePositionService.setPosition({ top: this.window.scrollY, left: this.window.scrollX });
    return true;
  }

  onFiltersClick(activeFilter?: string, activeType?: string) {
    let filter = [activeFilter];
    if (activeType) {
      if (this.searchResultsFilterablesService.wonderFilterableIds.includes(activeType)) {
        filter = ['wonder'];
      } else if (this.searchResultsFilterablesService.sharryFilterableIds.includes(activeType)) {
        filter = [];
        if (this.searchResultsFilterablesService.tourFilterableIds.includes(activeType)) {
          filter.push('tour');
        }
        if (this.searchResultsFilterablesService.experienceFilterableIds.includes(activeType)) {
          filter.push('experience');
        }
        if (this.searchResultsFilterablesService.itemFilterableIds.includes(activeType)) {
          filter.push('item');
        }
        if (this.searchResultsFilterablesService.rentalFilterableIds.includes(activeType)) {
          filter.push('rental');
        }
        if (this.searchResultsFilterablesService.accommodationFilterableIds.includes(activeType)) {
          filter.push('accommodation');
        }
      } else if (this.searchResultsFilterablesService.eventFilterableIds.includes(activeType)) {
        filter = ['event'];
      } else {
        filter = null;
      }
    }
    this.activeFilter = filter;
    this.cd.detectChanges;
    const params = { ...this.paramsSubj.value };
    if (params.beginDate && params.endDate) {
      params.dateRange = new DateRange<Date>(new Date(params.beginDate), new Date(params.endDate));
    } else {
      delete params.dateRange;
    }
    const types = parseArray(params.types);
    const searchQualifications = parseArray(params.searchQualifications);
    params.searchTypes = {
      sharry: (this.searchResultsFilterablesService.filterables?.sharry || [])
        ?.filter(k => types.includes(k.id))
        .map(f => f.id),
      tour: (this.searchResultsFilterablesService.filterables?.tour || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      experience: (this.searchResultsFilterablesService.filterables?.experience || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      item: (this.searchResultsFilterablesService.filterables?.item || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      rental: (this.searchResultsFilterablesService?.filterables?.rental || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      accommodation: (this.searchResultsFilterablesService?.filterables?.accommodation || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      wonder: (this.searchResultsFilterablesService.filterables?.wonder || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      event: (this.searchResultsFilterablesService.filterables?.event || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      users: (this.searchResultsFilterablesService.filterables?.users || [])
        .filter(k => searchQualifications.includes(k.id))
        .map(f => f.id),
    };
    params.searchAccessibilityCategories = parseArray(params.searchAccessibilityCategories);
    params.searchCategories = parseArray(params.searchCategories);
    this.dialog = this.openDialogService.openDialog(this.mobileFiltersDialog, {
      ...getDefaultModalSettings(),
      data: {
        ...params,
      },
    });
  }

  onClose() {
    this.dialog.close();
  }

  onSearch(params: Params, isMultipleTypes?: boolean) {
    this.headerSearchService.setBusy(true);
    this.cd.detectChanges();
    const typesObj = { ...params.searchTypes };
    params.types = [
      ...(typesObj.tour || []),
      ...(typesObj.experience || []),
      ...(typesObj.item || []),
      ...(typesObj.rental || []),
      ...(typesObj.accommodation || []),
      ...(typesObj.wonder || []),
      ...(typesObj.event || []),
    ];
    params.searchQualifications = [...(typesObj.users || [])];
    delete params.searchTypes;
    if (this.dialog) {
      this.dialog.close();
    }
    if (params.distance !== undefined && params.distance !== null) {
      if (params.distance === 0) {
        params.distance = undefined;
      } else {
        params.region = undefined;
      }
    }
    if (params.dateRange) {
      params.beginDate = params.dateRange.start.toISOString();
      params.endDate = params.dateRange.end.toISOString();
      delete params.dateRange;
    } else {
      params.beginDate = undefined;
      params.endDate = undefined;
    }
    if ((params?.types?.length === 1 || params?.searchQualifications?.length === 1) && !isMultipleTypes) {
      const isSearchQualification = params?.searchQualifications?.length;
      const type = isSearchQualification ? params.searchQualifications[0] : params.types[0];
      let ids;
      const filterables = this.searchResultsFilterablesService.filterables;
      for (const key of Object.keys(filterables)) {
        if (key === 'sharry' || (!isSearchQualification && key === 'users')) {
          continue;
        }
        const items = filterables[key as keyof SearchResultsFilterablesService['filterables']].map(item => item.id);
        if (items.includes(type)) {
          ids = items;
          break;
        }
      }
      if (isSearchQualification) {
        params.searchQualifications = ids;
      } else {
        params.types = ids;
      }
    }
    if (JSON.stringify(params) === JSON.stringify(this.oldQueryParams)) {
      this.headerSearchService.setBusy(false);
      this.cd.detectChanges();
      return;
    }
    this.oldQueryParams = params;
    this.paramsSubj.next(params);
    this.headerSearchService.setQueryParams(params);
    this.updateRoute({
      ...params,
    });
  }

  clearSearch() {
    this.filterChildren.forEach(child => {
      child.onCleanFilter();
      child.onClearList();
      import('src/app/_shared/components/generic-snack-bar/generic-snack-bar.component').then(
        ({ GenericSnackBarComponent }) =>
          this.openSnackBarService.openSnackBar(GenericSnackBarComponent, {
            data: { text: 'cleared-filters', close: true, customIcon: 'eraser', duration: 4000 },
          }),
      );
    });
  }

  updateRoute(params: Params) {
    this.searchEnginePositionService.setPosition({ top: 0, left: 0 });
    if (Object.keys(this.route.snapshot.params).length) {
      this.router.navigate([this.routeTranslateService.translateRoute('search-results')], {
        queryParams: {
          ...this.paramsSubj.value,
          ...params,
        },
        replaceUrl: true,
      });
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          ...this.paramsSubj.value,
          ...params,
        },
        replaceUrl: true,
      });
    }
  }

  checkMultipleTypes(types: any): boolean {
    if (types) {
      if (typeof types === 'string') {
        types = [types];
      }
      const lookup: { [key: string]: SharryKind } = {};
      this.searchResultsFilterablesService.filterablesInline.forEach((item: any) => {
        lookup[item.id] = item;
      });
      let comparisonValue: string;
      for (const id of types) {
        const item = lookup[id];
        let valueToCompare: string;
        if (item?.contentType === 'sharry' && item?.sharrySelectableKind) {
          valueToCompare = item?.sharrySelectableKind?.sharrySelection;
        } else {
          valueToCompare = item?.contentType;
        }
        if (comparisonValue === undefined) {
          comparisonValue = valueToCompare;
        } else if (comparisonValue !== valueToCompare) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  onKindSearch(type: any, isKind: boolean) {
    this.toggleSelection(type);
    const params = { ...this.paramsSubj.value };
    const isMultipleTypes =
      (typeof params?.types === 'string' ? false : params?.types?.length > 1) ||
      (typeof params?.searchQualifications === 'string' ? false : params?.searchQualifications?.length > 1);
    if (isKind) {
      if (this.selectedTypes.length > 0) {
        params.types = this.selectedTypes;
      } else {
        params.types = this._types();
      }
    } else {
      params.searchQualifications = [type];
    }
    const searchQualifications = parseArray(params.searchQualifications);
    const types = parseArray(params.types);
    params.searchTypes = {
      sharry: (this.searchResultsFilterablesService.filterables?.sharry || [])
        ?.filter(k => types.includes(k.id))
        .map(f => f.id),
      tour: (this.searchResultsFilterablesService.filterables?.tour || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      experience: (this.searchResultsFilterablesService.filterables?.experience || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      item: (this.searchResultsFilterablesService.filterables?.item || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      rental: (this.searchResultsFilterablesService?.filterables?.rental || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      accommodation: (this.searchResultsFilterablesService?.filterables?.accommodation || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      wonder: (this.searchResultsFilterablesService.filterables?.wonder || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      event: (this.searchResultsFilterablesService.filterables?.event || [])
        .filter(k => types.includes(k.id))
        .map(f => f.id),
      users: (this.searchResultsFilterablesService.filterables?.users || [])
        .filter(k => searchQualifications.includes(k.id))
        .map(f => f.id),
    };
    this.onSearch(params, isMultipleTypes);
  }

  selectedTypes: string[] = [];

  toggleSelection(type: string): void {
    const index = this.selectedTypes.indexOf(type);
    if (index > -1) {
      this.selectedTypes.splice(index, 1);
    } else {
      this.selectedTypes.push(type);
    }
    this.paramsService.saveSelectedTypes(this.selectedTypes);
  }

  onToggleMap(event: MouseEvent) {
    event.preventDefault();
    const params = { ...this.paramsSubj.value };
    const routes = [this.routeTranslateService.translateRoute('search-results')];
    if (params?.mode !== 'map') {
      routes.push('map');
    }
    this.router.navigate(routes, {
      queryParams: {
        ...params,
        mode: params.mode === 'map' ? 'list' : 'map',
      },
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
    const routes = [
      this.routeTranslateService.translateRoute('search-results'),
      this.routeTranslateService.translateRoute('sharry'),
      this.routeTranslateService.translateRoute('wonders'),
      this.routeTranslateService.translateRoute('events'),
      this.routeTranslateService.translateRoute('profile'),
    ];
    const fullUrl = this.router.url.split('?')[0].split('#')[0];
    const segments = fullUrl.split('/');
    const currentRoute = '/' + segments.slice(1, 3).join('/');
    if (!routes.includes(currentRoute)) {
      localStorage.removeItem('selectedTypes');
      localStorage.removeItem('types');
    }
  }
}

type SearchType = 'wonder' | 'sharry' | 'event' | 'users' | 'tour' | 'experience' | 'item' | 'rental' | 'accommodation';
